import { Reveal, RevealStagger, StaggerItem } from "../components/Reveal";
import { MagneticButton } from "../components/MagneticButton";
import { FinalCTA } from "../components/FinalCTA";
import { ProcessJourney } from "../components/ProcessJourney";
import { FloatingMotif } from "../components/FloatingMotif";
import { PROCESS } from "../data/site";

const DETAILS = [
    {
        n: "01",
        title: "Discovery",
        body: "We start with the business — not the website. Audience, offer, economics, and the real bottleneck behind enquiries.",
        deliverables: ["Stakeholder interviews", "Audience model", "Bottleneck analysis"],
        outcome: "A shared, written understanding of the business.",
    },
    {
        n: "02",
        title: "Strategy",
        body: "A clear plan: positioning, narrative, structure, and the specific decisions your visitors need to make.",
        deliverables: ["Positioning brief", "Narrative blueprint", "Decision architecture"],
        outcome: "An approved direction before a single pixel moves.",
    },
    {
        n: "03",
        title: "Build",
        body: "Editorial-grade design and engineering. Considered typography, content, and architecture — no template feel.",
        deliverables: ["Editorial design system", "Engineered components", "Content & assets"],
        outcome: "A digital presence that looks and feels custom.",
    },
    {
        n: "04",
        title: "Launch",
        body: "A controlled, measured rollout — with the trust signals, tracking, and search foundation in place from day one.",
        deliverables: ["Technical SEO setup", "Reputation system live", "Analytics & tracking"],
        outcome: "A confident launch — not a stressful one.",
    },
    {
        n: "05",
        title: "Optimize",
        body: "Quiet, ongoing refinement based on real behaviour. Your digital presence improves while you run the business.",
        deliverables: ["Monthly performance review", "Content stewardship", "Conversion refinements"],
        outcome: "A presence that compounds, every month.",
    },
];

export default function Process() {
    return (
        <div data-testid="page-process">
            {/* Header */}
            <section className="relative pt-12 md:pt-20 pb-20 md:pb-28 overflow-hidden">
                <FloatingMotif />
                <div className="max-w-[1440px] mx-auto px-6 md:px-12 relative">
                    <div className="grid grid-cols-1 md:grid-cols-12 gap-10">
                        <div className="md:col-span-8">
                            <Reveal>
                                <div className="flex items-center gap-4 mb-8">
                                    <span className="h-px w-10 bg-[#111111]" />
                                    <span className="text-[10px] tracking-[0.28em] uppercase font-medium">
                                        Process · Five stages
                                    </span>
                                </div>
                            </Reveal>
                            <Reveal delay={0.05}>
                                <h1 className="font-serif text-5xl sm:text-6xl md:text-7xl lg:text-[92px] leading-[0.98] tracking-tight">
                                    A clear,
                                    <br />
                                    <em>well-rehearsed process.</em>
                                </h1>
                            </Reveal>
                            <Reveal delay={0.15}>
                                <p className="mt-10 text-lg md:text-xl text-[#444444] max-w-2xl leading-relaxed">
                                    Five quiet stages. Each one builds on the
                                    last. Discipline, not urgency — because
                                    business decisions of this kind benefit
                                    from being made slowly.
                                </p>
                            </Reveal>
                            <Reveal delay={0.25}>
                                <div className="mt-12 flex flex-wrap gap-4">
                                    <MagneticButton
                                        to="/contact"
                                        testid="process-cta-primary"
                                    >
                                        Begin with Discovery
                                    </MagneticButton>
                                    <MagneticButton
                                        to="/services"
                                        testid="process-cta-secondary"
                                        variant="outline"
                                    >
                                        View Services
                                    </MagneticButton>
                                </div>
                            </Reveal>
                        </div>
                        <div className="md:col-span-4">
                            <Reveal delay={0.1}>
                                <div className="border border-[#dddddd] bg-white p-6">
                                    <p className="text-[10px] tracking-[0.28em] uppercase text-[#444444]">
                                        Map · Five stages
                                    </p>
                                    <ol className="mt-5 space-y-3">
                                        {PROCESS.map((p) => (
                                            <li
                                                key={p.n}
                                                className="flex items-baseline gap-3 border-b border-[#dddddd] pb-3 last:border-0 last:pb-0"
                                            >
                                                <span className="font-serif italic text-base text-[#444444] w-6">
                                                    {p.n}
                                                </span>
                                                <span className="font-serif text-lg text-[#111111]">
                                                    {p.title}
                                                </span>
                                            </li>
                                        ))}
                                    </ol>
                                </div>
                            </Reveal>
                        </div>
                    </div>
                </div>
            </section>

            {/* Horizontal journey */}
            <section className="border-y border-[#dddddd] bg-white py-20 md:py-24">
                <div className="max-w-[1440px] mx-auto px-6 md:px-12">
                    <div className="flex items-end justify-between mb-12">
                        <div>
                            <p className="text-[10px] tracking-[0.28em] uppercase text-[#444444]">
                                § The Journey
                            </p>
                            <h2 className="mt-3 font-serif text-3xl md:text-4xl">
                                Discovery → Growth.
                            </h2>
                        </div>
                        <p className="hidden md:block font-serif italic text-[#444444]">
                            Approx. 4–8 weeks
                        </p>
                    </div>
                    <ProcessJourney />
                </div>
            </section>

            {/* Detailed stages */}
            <section className="bg-[#fafafa] py-20 md:py-28">
                <div className="max-w-[1440px] mx-auto px-6 md:px-12">
                    <RevealStagger className="border-t border-[#111111]">
                        {DETAILS.map((d) => (
                            <StaggerItem key={d.n}>
                                <div
                                    data-testid={`process-page-step-${d.n}`}
                                    className="grid grid-cols-12 gap-6 md:gap-10 py-14 md:py-20 border-b border-[#dddddd]"
                                >
                                    <div className="col-span-2 md:col-span-1 font-serif italic text-4xl md:text-6xl text-[#444444]">
                                        {d.n}
                                    </div>
                                    <div className="col-span-10 md:col-span-4">
                                        <h2 className="font-serif text-4xl md:text-6xl text-[#111111] leading-[1.05]">
                                            {d.title}
                                        </h2>
                                        <p className="mt-6 text-[#444444] text-lg leading-relaxed">
                                            {d.body}
                                        </p>
                                    </div>
                                    <div className="col-span-12 md:col-span-4">
                                        <p className="text-[10px] tracking-[0.28em] uppercase text-[#444444] mb-4">
                                            Deliverables
                                        </p>
                                        <ul className="space-y-3">
                                            {d.deliverables.map((x) => (
                                                <li
                                                    key={x}
                                                    className="flex items-baseline gap-3 border-b border-[#dddddd] pb-3"
                                                >
                                                    <span aria-hidden className="text-[#111111]">
                                                        ◇
                                                    </span>
                                                    <span className="text-[#111111] text-base">
                                                        {x}
                                                    </span>
                                                </li>
                                            ))}
                                        </ul>
                                    </div>
                                    <div className="col-span-12 md:col-span-3">
                                        <p className="text-[10px] tracking-[0.28em] uppercase text-[#444444] mb-4">
                                            Outcome
                                        </p>
                                        <p className="font-serif italic text-xl md:text-2xl text-[#111111] leading-snug">
                                            {d.outcome}
                                        </p>
                                    </div>
                                </div>
                            </StaggerItem>
                        ))}
                    </RevealStagger>
                </div>
            </section>

            {/* Note */}
            <section className="py-24 md:py-32">
                <div className="max-w-[1440px] mx-auto px-6 md:px-12">
                    <div className="grid grid-cols-1 md:grid-cols-12 gap-10">
                        <div className="md:col-span-3">
                            <p className="text-[10px] tracking-[0.28em] uppercase text-[#444444]">
                                § Note
                            </p>
                        </div>
                        <div className="md:col-span-9">
                            <Reveal>
                                <p className="font-serif text-3xl md:text-5xl leading-[1.15] text-[#111111]">
                                    The work is not the website.{" "}
                                    <em className="text-[#444444]">
                                        The work is the way a stranger thinks
                                        about your business after thirty
                                        seconds on it.
                                    </em>
                                </p>
                            </Reveal>
                        </div>
                    </div>
                </div>
            </section>

            <FinalCTA />
        </div>
    );
}
